<script setup lang="ts">
import { ALabel, ALanguageInput } from '@atabix/vue-ui'
import type { LanguageItem } from '@atabix/vue-ui/src/components/ui/input/ALanguageInput.vue'
import { openErrorDialog } from '@atabix/vue-ui/src/lib/dialog/utils'
import { computed, type HTMLAttributes, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { LocaleService } from '@/helpers/LocaleService'
import { User } from '@/models'
import { useSettingsStore } from '@/stores'
import { useAuthStore } from '@/stores/auth'

const properties = defineProps<{
  label?: string
  ariaLabel?: string
  class?: HTMLAttributes['class']
}>()

export interface LanguageSelectEmits {
  (event: 'languageChanged', language: string | undefined): void
}

const emit = defineEmits<LanguageSelectEmits>()

const settings = useSettingsStore()
const locale = ref<string | undefined>(settings.locale)
const auth = useAuthStore()

const { t } = useI18n({
  messages: {
    en: {
      'Select language': 'Select language',
    },
    nl: {
      'Select language': 'Selecteer taal',
    },
    de: {
      'Select language': 'Sprache auswählen',
    },
  },
})

const languages = computed((): LanguageItem[] => {
  return settings.settings?.languages ?? []
})

async function updateLocale(locale: string | undefined) {
  if (!auth.user) {
    LocaleService.setAppLocale(locale)
    emit('languageChanged', locale)
    return
  }
  try {
    const user = await auth.user.update({ language: locale })
    await auth.setUser(new User().hydrate({ ...auth.user, language: user.language }))
    emit('languageChanged', locale)
  } catch (error) {
    console.error(error)
    openErrorDialog({ title: t('unexpected error occurred'), description: t('error changing {item}', { item: t('language', 1) }) })
  }
}
</script>

<template>
  <div class="language-select">
    <ALabel v-if="label">{{ label }}</ALabel>
    <ALanguageInput
      :key="`language-${languages?.[0].name}`"
      v-model="locale"
      :aria-label="ariaLabel || label"
      :class="properties.class"
      :items="languages"
      :placeholder="t('Select language')"
      @update:model-value="updateLocale"
    />
  </div>
</template>

<style scoped>
.language-select {
  @apply flex flex-col gap-y-3;
}
</style>
